import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Container, Grid, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomSwitch from "../../../components/CustomSwitch";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import {
  createCategoryByType,
  createSubCategoryByCategoryId,
  deleteCategoryById,
  deleteSubCategoryById,
  getCategoriesWoLoader,
  getSubCategoriesByCategoryId,
  updateCategoryActiveById,
  updateCategoryByTypeAndId,
  updateSubCategoryActiveById,
  updateSubCategoryById,
} from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { delBtn, plusBtn } from "../../../util/CommonIconStyle";
import { ServiceTypes } from "../../../util/dropList";
import { patchMandateSession } from "../../../util/mandateSessionStorage";
import { canWriteModule } from "../../../util/roleModuleAccess";

const usageInList = [
  { name: "Administered", value: "administered" },
  { name: "Consumables", value: "consumables" },
  { name: "Prescription", value: "prescription" },
];

const usageInAllOption = { name: "All", value: "All" };
const usageInOptions = [usageInAllOption, ...usageInList];
const usageInValuesOnly = usageInList.map((li) => li.value);
const defaultUsageIn = ["All", ...usageInValuesOnly];

const getUsageInValue = (item) => {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  if (typeof item === "object") {
    return String(item?.value ?? item?.name ?? "");
  }
  return "";
};

const mapUsageInValues = (raw) => {
  try {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const vals = raw
      .map(getUsageInValue)
      .filter(Boolean)
      .filter(
        (val, index, arr) =>
          arr.findIndex((v) => v.toLowerCase() === val.toLowerCase()) === index,
      );
    const hasAll = vals.some((v) => v.toLowerCase() === "all");
    const selected = usageInValuesOnly.filter((opt) =>
      vals.some((v) => v.toLowerCase() === opt.toLowerCase()),
    );
    if (hasAll || selected.length === usageInValuesOnly.length) {
      return [...defaultUsageIn];
    }
    return selected;
  } catch {
    return [];
  }
};

const toUsageInPayload = (selected) => {
  try {
    const list = Array.isArray(selected) ? selected : [];
    const vals = list.map(getUsageInValue).filter(Boolean);
    if (vals.some((v) => v.toLowerCase() === "all")) {
      return [...usageInValuesOnly];
    }
    return usageInValuesOnly.filter((opt) =>
      vals.some((v) => v.toLowerCase() === opt.toLowerCase()),
    );
  } catch {
    return [...usageInValuesOnly];
  }
};

const normalizeUsageInSelection = (selected, previous = []) => {
  try {
    const next = (Array.isArray(selected) ? selected : [])
      .map(getUsageInValue)
      .filter(Boolean);
    const prev = (Array.isArray(previous) ? previous : [])
      .map(getUsageInValue)
      .filter(Boolean);
    const hadAll = prev.some((v) => v.toLowerCase() === "all");
    const hasAll = next.some((v) => v.toLowerCase() === "all");
    const individuals = next.filter((v) => v.toLowerCase() !== "all");

    // Check All → select every option
    if (hasAll && !hadAll) return [...defaultUsageIn];
    // Uncheck All → clear every option
    if (!hasAll && hadAll) return [];

    // Unchecked an item while All was still present → drop All, keep remaining
    if (hasAll && individuals.length < usageInValuesOnly.length) {
      return individuals.filter((v) =>
        usageInValuesOnly.some((opt) => opt.toLowerCase() === v.toLowerCase()),
      );
    }

    // Manually selected every option → mark All as selected too
    if (individuals.length === usageInValuesOnly.length) {
      return [...defaultUsageIn];
    }

    return individuals.filter((v) =>
      usageInValuesOnly.some((opt) => opt.toLowerCase() === v.toLowerCase()),
    );
  } catch {
    return [...defaultUsageIn];
  }
};

const initValues = {
  name: "",
  categoryType: null,
  usageIn: [...defaultUsageIn],
};
const initErrs = { name: false, categoryType: false, usageIn: false };
const initHlps = { name: "", categoryType: "", usageIn: "" };

const tableHeaders = ["category", "usageIn", "activeStatus", "extraStatus"];
const serviceTblHdrs = [
  "category",
  "categoryType",
  "activeStatus",
  "extraStatus",
];
const subCategoryTblHdrs = ["subCategory", "activeStatus", "extraStatus"];

const toCategoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const lockedStockCategoryNames = ["vaccines", "vaccination", "deworming"];
const lockedServiceCategoryNames = [
  "lab",
  "radiology",
  "hygiene & grooming",
  "vaccines",
  "vaccination",
  "preventive care",
];

const normalizeCategoryName = (name) =>
  String(name ?? "")
    .trim()
    .toLowerCase();

const isLockedCategory = (name, isServices) => {
  const normalized = normalizeCategoryName(name);
  if (!normalized) return false;
  const locked = isServices
    ? lockedServiceCategoryNames
    : lockedStockCategoryNames;
  return locked.includes(normalized);
};

const InventorySettingsCategory = ({ categoryType = "stock" }) => {
  const dispatch = useDispatch();
  const canManageInventory = canWriteModule("inventory");
  const isServices = categoryType === "services" || categoryType === "service";
  const [searchValue, setSearchValue] = useState("");
  const [modVsble, setModVsble] = useState(false);
  const [delModVsble, setDelModVsble] = useState(false);
  const [statusModVsble, setStatusModVsble] = useState(false);
  const [categoryValues, setCategoryValues] = useState(initValues);
  const [categoryErrors, setCategoryErrors] = useState(initErrs);
  const [categoryHelps, setCategoryHelps] = useState(initHlps);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pendingActiveStatus, setPendingActiveStatus] = useState(null);
  const [pendingStatusType, setPendingStatusType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [mandateTick, setMandateTick] = useState(0);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const categories = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const list = Array.isArray(reqData?.categories) ? reqData.categories : [];
      return list.filter((loc) => loc?.type === categoryType);
    } catch {
      return [];
    }
  }, [mandateDataRaw, mandateTick, categoryType]);

  useEffect(() => {
    setSelectedCategory(null);
    setSubCategories([]);
    setSearchValue("");
    onApiCall();
  }, [dispatch, categoryType]);

  useEffect(() => {
    if (selectedCategory?.id) {
      onSubCategoryApiCall(selectedCategory.id);
    }
  }, [selectedCategory?.id]);

  const onApiCall = () => {
    try {
      dispatch(getCategoriesWoLoader()).then((res) => {
        try {
          const payload = res?.payload;
          if (payload == null) return;
          patchMandateSession({
            categories: toCategoryList(payload),
          });
          setMandateTick((t) => t + 1);
        } catch {
          // ignore mandate patch errors
        }
      });
    } catch {
      // ignore category refresh errors
    }
  };

  const onSubCategoryApiCall = (categoryId) => {
    try {
      dispatch(getSubCategoriesByCategoryId(categoryId)).then((res) => {
        if (res?.payload) {
          setSubCategories(
            Array.isArray(res.payload)
              ? res.payload
              : (res.payload?.data ?? []),
          );
        } else {
          setSubCategories([]);
        }
      });
    } catch {
      // ignore sub category refresh errors
    }
  };

  const handleCreate = () => {
    setCategoryValues({ ...initValues, usageIn: [...defaultUsageIn] });
    setCategoryErrors(initErrs);
    setCategoryHelps(initHlps);
    setSelectedItem(null);
    setModVsble(true);
  };

  const handleModClose = () => {
    setModVsble(false);
    setCategoryValues({ ...initValues, usageIn: [...defaultUsageIn] });
    setCategoryErrors(initErrs);
    setCategoryHelps(initHlps);
    setSelectedItem(null);
  };

  const handleChange = (name, value) => {
    const nextValue =
      name === "usageIn"
        ? normalizeUsageInSelection(value, categoryValues?.usageIn)
        : value;
    const reqValue = Array.isArray(nextValue)
      ? nextValue
      : typeof nextValue === "object"
        ? nextValue?.value
        : nextValue;
    setCategoryValues({ ...categoryValues, [name]: nextValue });
    if (name === "usageIn") {
      setCategoryErrors({ ...categoryErrors, usageIn: false });
      setCategoryHelps({ ...categoryHelps, usageIn: "" });
      return;
    }
    setCategoryErrors({
      ...categoryErrors,
      [name]: !reqValue || reqValue?.length === 0,
    });
    setCategoryHelps({
      ...categoryHelps,
      [name]:
        !reqValue || reqValue?.length === 0 ? "This is required field" : "",
    });
  };

  const handleEdit = (loc) => {
    if (!selectedCategory && isLockedCategory(loc?.name, isServices)) return;
    const reqObj = {
      ...categoryValues,
      name: loc?.name ?? "",
      categoryType: isServices ? (loc?.categoryType ?? null) : null,
      usageIn: !isServices ? mapUsageInValues(loc?.usageIn) : [],
    };
    setCategoryValues(reqObj);
    setSelectedItem(loc);
    setModVsble(true);
  };

  const handleDelete = (loc) => {
    if (!selectedCategory && isLockedCategory(loc?.name, isServices)) return;
    setSelectedItem(loc);
    setDelModVsble(true);
  };

  const handleChangeCategorySwitch = (loc) => {
    if (!loc?.id) return;
    setSelectedItem(loc);
    setPendingActiveStatus(!isActiveRecord(loc));
    setPendingStatusType("category");
    setStatusModVsble(true);
  };

  const handleChangeSubCategorySwitch = (loc) => {
    if (!loc?.id) return;
    setSelectedItem(loc);
    setPendingActiveStatus(!isActiveRecord(loc));
    setPendingStatusType("subCategory");
    setStatusModVsble(true);
  };

  const handleCloseStatusMod = () => {
    setStatusModVsble(false);
    setPendingActiveStatus(null);
    setPendingStatusType(null);
    setSelectedItem(null);
  };

  const handleConfirmStatusUpdate = () => {
    if (!selectedItem?.id || pendingActiveStatus == null || !pendingStatusType)
      return;

    if (pendingStatusType === "subCategory") {
      dispatch(
        updateSubCategoryActiveById({
          id: selectedItem.id,
          active: pendingActiveStatus === true,
        }),
      ).then((res) => {
        if (res?.payload) {
          if (selectedCategory?.id) {
            onSubCategoryApiCall(selectedCategory.id);
          }
          onApiCall();
          handleCloseStatusMod();
        }
      });
      return;
    }

    dispatch(
      updateCategoryActiveById({
        id: selectedItem.id,
        active: pendingActiveStatus === true,
      }),
    ).then((res) => {
      if (res?.payload) {
        onApiCall();
        handleCloseStatusMod();
      }
    });
  };

  const handleOpenSubCategory = (loc) => {
    setSearchValue("");
    setSelectedCategory(loc);
  };

  const handleBackToCategory = () => {
    setSelectedCategory(null);
    setSubCategories([]);
    setSearchValue("");
    handleModClose();
    setDelModVsble(false);
    setStatusModVsble(false);
    setPendingActiveStatus(null);
    setPendingStatusType(null);
    setSelectedItem(null);
  };

  const getTableCategories = () => {
    return (Array.isArray(categories) ? categories : [])
      ?.filter((loc) =>
        loc?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      )
      ?.map((loc) => ({
        category: (
          <div
            className="cursor blue-color underline fs14 font-bold"
            onClick={() => handleOpenSubCategory(loc)}
          >
            {loc?.name}
          </div>
        ),
        name: loc?.name,
        categoryType: loc?.categoryType ?? "",
        usageIn: loc?.usageIn?.join(", "),
        activeStatus: (
          <div className="flex-row-ali-cen">
            {canManageInventory ? (
              <CustomSwitch
                value={isActiveRecord(loc)}
                onChange={() => handleChangeCategorySwitch(loc)}
                greenToGray
              />
            ) : null}
            <div
              style={{ width: 80 }}
              className={canManageInventory ? "ml10" : ""}
            >
              {isActiveRecord(loc) ? (
                <Typography className="green4 grn-bck status-card fs12 font-medium">
                  Active
                </Typography>
              ) : (
                <Typography className="red11 red-back status-card fs12 font-medium">
                  Inactive
                </Typography>
              )}
            </div>
          </div>
        ),
        extraStatus: canManageInventory ? (
          <div className="flex-row flex1-end">
            {!isLockedCategory(loc?.name, isServices) ? (
              <>
                <div className="cursor" onClick={() => handleDelete(loc)}>
                  <div className="flex-center">
                    <DeleteOutlineIcon sx={delBtn} />
                  </div>
                  <Typography className="red3 fs8 txt-semi-bold text-align-center">
                    Delete
                  </Typography>
                </div>
                <div className="cursor ml10" onClick={() => handleEdit(loc)}>
                  <div className="flex-center">
                    <EditIcon sx={plusBtn} />
                  </div>
                  <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                    Edit
                  </Typography>
                </div>
              </>
            ) : null}
          </div>
        ) : null,
      }));
  };

  const getTableSubCategories = () => {
    return (Array.isArray(subCategories) ? subCategories : [])
      ?.filter((loc) =>
        loc?.name?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      )
      ?.map((loc) => ({
        category: loc?.name,
        subCategory: loc?.name,
        name: loc?.name,
        activeStatus: (
          <div className="flex-row-ali-cen">
            {canManageInventory ? (
              <CustomSwitch
                value={isActiveRecord(loc)}
                onChange={() => handleChangeSubCategorySwitch(loc)}
                greenToGray
              />
            ) : null}
            <div
              style={{ width: 80 }}
              className={canManageInventory ? "ml10" : ""}
            >
              {isActiveRecord(loc) ? (
                <Typography className="green4 grn-bck status-card fs12 font-medium">
                  Active
                </Typography>
              ) : (
                <Typography className="red11 red-back status-card fs12 font-medium">
                  Inactive
                </Typography>
              )}
            </div>
          </div>
        ),
        extraStatus: canManageInventory ? (
          <div className="flex-row flex1-end">
            <div className="cursor" onClick={() => handleDelete(loc)}>
              <div className="flex-center">
                <DeleteOutlineIcon sx={delBtn} />
              </div>
              <Typography className="red3 fs8 txt-semi-bold text-align-center">
                Delete
              </Typography>
            </div>
            <div className="cursor ml10" onClick={() => handleEdit(loc)}>
              <div className="flex-center">
                <EditIcon sx={plusBtn} />
              </div>
              <Typography className="blue-color fs8 txt-semi-bold text-align-center">
                Edit
              </Typography>
            </div>
          </div>
        ) : null,
      }));
  };

  const handleValidate = () => {
    let errObj = {};
    let hlpObj = {};
    const fieldsToValidate = isServices
      ? isSubView
        ? ["name"]
        : ["name", "categoryType"]
      : isSubView
        ? ["name"]
        : ["name"];
    fieldsToValidate.forEach((key) => {
      const reqValue = Array.isArray(categoryValues?.[key])
        ? categoryValues?.[key]
        : typeof categoryValues?.[key] === "object"
          ? categoryValues?.[key]?.value
          : categoryValues?.[key];
      if (!reqValue || reqValue?.length === 0) {
        errObj = {
          ...errObj,
          [key]: true,
        };
        hlpObj = {
          ...hlpObj,
          [key]: "This is required field",
        };
      }
    });
    setCategoryErrors({ ...categoryErrors, ...errObj });
    setCategoryHelps({ ...categoryHelps, ...hlpObj });
    return Object.values(errObj)?.some((err) => err === true);
  };

  const handleSave = () => {
    if (handleValidate()) return;
    if (selectedCategory) {
      const data = { name: categoryValues?.name };
      if (selectedItem) {
        dispatch(updateSubCategoryById({ id: selectedItem?.id, data })).then(
          (res) => {
            if (res?.payload) {
              onSubCategoryApiCall(selectedCategory.id);
              onApiCall();
              handleModClose();
            }
          },
        );
      } else {
        dispatch(
          createSubCategoryByCategoryId({
            categoryId: selectedCategory.id,
            data,
          }),
        ).then((res) => {
          if (res?.payload) {
            onSubCategoryApiCall(selectedCategory.id);
            onApiCall();
            handleModClose();
          }
        });
      }
      return;
    }
    const data = { name: categoryValues?.name, type: categoryType };
    if (isServices) {
      data.categoryType =
        typeof categoryValues?.categoryType === "object"
          ? categoryValues?.categoryType?.value
          : categoryValues?.categoryType;
    } else {
      data.usageIn = toUsageInPayload(categoryValues?.usageIn);
    }
    if (selectedItem) {
      dispatch(updateCategoryByTypeAndId({ id: selectedItem?.id, data })).then(
        (res) => {
          if (res?.payload) {
            onApiCall();
            handleModClose();
          }
        },
      );
    } else {
      dispatch(createCategoryByType(data)).then((res) => {
        if (res?.payload) {
          onApiCall();
          handleModClose();
        }
      });
    }
  };

  const handleCloseDelMod = () => {
    setDelModVsble(false);
    setSelectedItem(null);
  };

  const handleDeleteCategory = () => {
    if (selectedCategory) {
      dispatch(deleteSubCategoryById(selectedItem?.id)).then((res) => {
        if (res?.payload) {
          onSubCategoryApiCall(selectedCategory.id);
          onApiCall();
          handleCloseDelMod();
        }
      });
      return;
    }
    dispatch(deleteCategoryById(selectedItem?.id)).then((res) => {
      if (res?.payload) {
        onApiCall();
        handleCloseDelMod();
      }
    });
  };

  const isSubView = Boolean(selectedCategory);

  return (
    <Container maxWidth="xl">
      <div className="back-white pv10">
        {isSubView ? (
          <div className="flex-row-ali-cen pos-rel mh10 mb10">
            <div
              className="flex-row-ali-cen cursor"
              onClick={handleBackToCategory}
            >
              <ArrowBackIcon
                sx={{ color: AppColors.appPrimary, width: 20, height: 20 }}
              />
              <Typography className="fs16 txt-semi-bold blue-color ml5">
                Back
              </Typography>
            </div>
            <Typography
              className="fs16 txt-semi-bold blue-color"
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              {selectedCategory?.name} - Subcategory
            </Typography>
          </div>
        ) : null}

        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={12}
          xl={12}
          className="flex-row-center"
        >
          <div className="flex-row">
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              style={{ width: "400px" }}
            >
              <CustomTextField
                placeholder="Search"
                fullWidth
                backgroundColor={AppColors.lightBlue2}
                value={searchValue}
                handleChange={(e) => setSearchValue(e.target.value)}
              />
            </Grid>
          </div>
          {canManageInventory ? (
            <div className="w5Per ml20">
              <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                <div>
                  <CustomButton text="Create" smallBtn onClick={handleCreate} />
                </div>
              </Grid>
            </div>
          ) : null}
        </Grid>

        <CustomTable
          columns={
            isSubView
              ? subCategoryTblHdrs
              : isServices
                ? serviceTblHdrs
                : tableHeaders
          }
          datas={isSubView ? getTableSubCategories() : getTableCategories()}
          grey
          maxHeight={isSubView ? "58vh" : "65vh"}
        />
      </div>

      <CustomModal
        open={modVsble}
        onClose={handleModClose}
        header={
          selectedItem
            ? isSubView
              ? "Edit Subcategory"
              : "Edit"
            : isSubView
              ? "Create Subcategory"
              : "Create"
        }
        modalWidth={30}
        rightModal
      >
        <Grid container spacing={2} className="ph20">
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                name="name"
                fullWidth
                value={categoryValues?.name}
                error={categoryErrors?.name}
                helperText={categoryHelps?.name}
                handleChange={(e) => {
                  const val = e?.target?.value?.replace(/[^a-zA-Z0-9 ]/g, "");
                  handleChange("name", val);
                }}
                label="Name"
                required
              />
            </div>
          </Grid>
          {!isServices && !isSubView ? (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt5">
                <CustomSelect
                  list={usageInOptions}
                  value={
                    Array.isArray(categoryValues?.usageIn)
                      ? categoryValues.usageIn
                      : []
                  }
                  error={categoryErrors?.usageIn}
                  helperText={categoryHelps?.usageIn}
                  label="Usage In"
                  handleChange={(e) =>
                    handleChange(
                      "usageIn",
                      Array.isArray(e?.target?.value) ? e.target.value : [],
                    )
                  }
                  multiSelectTagCheck
                />
              </div>
            </Grid>
          ) : null}
          {isServices && !isSubView ? (
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt5">
                <CustomSelect
                  list={ServiceTypes}
                  value={categoryValues?.categoryType}
                  error={categoryErrors?.categoryType}
                  helperText={categoryHelps?.categoryType}
                  label="Type *"
                  handleChange={(e) =>
                    handleChange("categoryType", e?.target?.value)
                  }
                  select
                />
              </div>
            </Grid>
          ) : null}
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end mt10">
              <div>
                <CustomButton
                  text={selectedItem ? "Update" : "Submit"}
                  smallBtn
                  onClick={handleSave}
                />
              </div>
            </div>
          </Grid>
        </Grid>
      </CustomModal>

      <CustomModal
        open={delModVsble}
        onClose={handleCloseDelMod}
        header={isSubView ? "Delete Sub Category" : "Delete Category"}
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          {isSubView
            ? "Are you sure you want to delete the sub category?"
            : "Are you sure you want to delete the category?"}
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="clinic-mod-btn-pos mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                redBtn
                onClick={handleDeleteCategory}
                smallBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseDelMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>

      <CustomModal
        open={statusModVsble}
        onClose={handleCloseStatusMod}
        header="Update Status"
        modal
        modalWidth={40}
      >
        <Typography className="txt-regular fs16 ml10">
          Are you sure you want to update the status?
        </Typography>
        <Typography className="txt-regular fs14 ml10 mt10">
          This will affect the existing inventory configurations.
        </Typography>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="flex1-center mb10 mt15">
            <div className="mr10">
              <CustomButton
                text="Yes"
                onClick={handleConfirmStatusUpdate}
                smallBtn
                redBtn
              />
            </div>
            <div className="ml10">
              <CustomButton text="No" onClick={handleCloseStatusMod} smallBtn />
            </div>
          </div>
        </Grid>
      </CustomModal>
    </Container>
  );
};

export default InventorySettingsCategory;
